import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { downloadFromBackend } from "../../lib/download";
import MarginUploadModal from "../../components/MarginUploadModal";
import { displayProduct } from "../../lib/products";
import { Can } from "../../lib/rbac";

/**
 * Global Margins page — lists every margin rule across every customer.
 *
 * Why this page exists: previously the "Template" + "Upload Excel" buttons
 * were buried three clicks deep on a customer-detail page, even though the
 * Excel template carries a Customer column with all customers as a dropdown
 * (so a single upload patches ANY number of customers). This page puts the
 * upload flow + a global view of all rules at top-level visibility.
 *
 * Filters:
 *   q          — free-text search across customer / station / city / province
 *   customer   — single-customer scope
 *   network    — esso / pipeline / ANY
 *   scope      — default / province / city / station
 *   product    — DIESEL_LS / DSL_EFF_LS / RFR / DEFD / GAS / ANY
 *
 *              GAS is the single margin-family key that applies to
 *              the three gasoline transaction canonicals GAS_REG /
 *              GAS_MID / GAS_PREM via the backend
 *              `pricing._GAS_TX_PRODUCTS` whitelist. Operators create
 *              exactly ONE GAS rule per customer — never per grade.
 *              (P4-PETRO-PASS-GAS-OPERATOR-UI-READINESS R1, 2026-09-16.)
 */
export default function Margins() {
  const [rules, setRules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marginUpload, setMarginUpload] = useState(null);
  const [defUpload, setDefUpload] = useState(null);

  // Re-apply DEF margins modal (Option B — Cause #2 hardening 2026-06-01).
  // Opens with a live preview of every DEFD tx whose stored margin
  // diverges from what the engine returns today. Operator must type
  // exactly `REAPPLY <N> DEFD` to enable the destructive button.
  //   Shape: { open, preview, typed, busy, include_invoiced }
  // where preview = { affected_count, total_litres, total_delta_cad,
  //                   customers: [...], transactions: [...] }
  const [reapplyModal, setReapplyModal] = useState({
    open: false, preview: null, typed: "", busy: false,
    include_invoiced: false,
  });

  // Filters
  const [q, setQ] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fNetwork, setFNetwork] = useState("");
  const [fScope, setFScope] = useState("");
  const [fProduct, setFProduct] = useState("");
  // "Effective on date" filter — when set, the table collapses to only the
  // rule that was actually in effect for each unique
  // (customer × scope × network × product × location) on this date.
  // Older rules with the same key (and any rules whose effective_date is
  // AFTER this date) are hidden. Empty string = show everything (default).
  const [asOfDate, setAsOfDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // /api/margins is admin-only; if a customer_id filter is set, scope to that
      // customer so the table shows ALL rules (otherwise we paginate-via-customer).
      // No backend pagination yet — fine for current scale (~100s of rules).
      const promises = fCustomer
        ? [api.get(`/margins?customer_id=${fCustomer}`)]
        : customers.map((c) => api.get(`/margins?customer_id=${c.customer_id}`));
      const results = await Promise.all(promises);
      const merged = results.flatMap((r) => r.data || []);
      setRules(merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load margin rules");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customers");
        setCustomers(data || []);
      } catch { /* leave empty */ }
    })();
  }, []);

  // Reload whenever the customer filter changes OR after the customer list arrives.
  useEffect(() => {
    if (customers.length > 0 || fCustomer) load();
    // eslint-disable-next-line
  }, [customers, fCustomer]);

  const customerNameById = useMemo(() => {
    const m = {};
    for (const c of customers) m[c.customer_id] = c.company_name;
    return m;
  }, [customers]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    // Pass 1: cheap filters (q / network / scope / product). If an as-of date
    // is set we drop rules effective AFTER that date here too — anything with
    // a future effective_date relative to the as-of cannot have been in
    // effect on it.
    let rows = rules.filter((r) => {
      if (fNetwork && r.network !== fNetwork) return false;
      if (fScope && r.scope !== fScope) return false;
      if (fProduct && r.product_canonical !== fProduct) return false;
      if (asOfDate && r.effective_date && r.effective_date > asOfDate) return false;
      if (!ql) return true;
      const haystack = [
        customerNameById[r.customer_id] || "",
        r.station_id || "",
        r.station_name || "",
        r.city || "",
        r.station_city || "",
        r.province || "",
      ].join(" ").toLowerCase();
      return haystack.includes(ql);
    });

    // Pass 2: when "effective on date" is set, collapse each
    // customer/scope/network/product/location group to its single most-recent
    // rule (greatest effective_date ≤ asOfDate). This mirrors the pricing
    // engine's `lookup_customer_margin` priority — what the customer would
    // ACTUALLY have been billed on that date.
    if (asOfDate) {
      const winners = new Map();
      for (const r of rows) {
        const key = [
          r.customer_id, r.scope, r.network, r.product_canonical || "ANY",
          r.station_id || "", r.city || "", r.province || "",
        ].join("|");
        const cur = winners.get(key);
        if (!cur || (r.effective_date || "") > (cur.effective_date || "")) {
          winners.set(key, r);
        }
      }
      rows = [...winners.values()];
    }
    return rows;
  }, [rules, q, fNetwork, fScope, fProduct, asOfDate, customerNameById]);

  const downloadTemplate = async () => {
    try {
      const filename = await downloadFromBackend("/margins/template.xlsx", {});
      toast.success(`Template downloaded · ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const downloadDefTemplate = async () => {
    try {
      const filename = await downloadFromBackend("/margins/def-template.xlsx", {});
      toast.success(`DEF template downloaded · ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const removeRule = async (margin_id) => {
    if (!window.confirm("Delete this margin rule?")) return;
    try {
      await api.delete(`/margins/${margin_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Open the Re-apply DEF margins modal: fetch the live drift preview
  // first so the operator sees the exact blast radius (per-customer
  // breakdown + $ delta total) BEFORE typing the confirmation. Pure
  // read — never writes. See backend `/margins/reapply-defd/preview`.
  const openReapplyDefModal = async () => {
    try {
      const { data } = await api.get("/margins/reapply-defd/preview");
      if (!data.affected_count) {
        toast.info(
          "All DEFD transactions are already in sync with the current "
          + "margin rules. Nothing to re-apply.",
        );
        return;
      }
      setReapplyModal({
        open: true, preview: data, typed: "", busy: false,
        include_invoiced: false,
      });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Failed to load DEFD drift preview${status ? ` (${status})` : ""}`,
      );
    }
  };

  // Execute the reapply after the modal's typed confirmation passes.
  const executeReapplyDef = async () => {
    if (!reapplyModal.preview) return;
    setReapplyModal((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post(
        "/margins/reapply-defd",
        null,
        {
          params: {
            confirm: "DELETE",  // server-side safety guard (typed UI guard is the modal itself)
            include_invoiced: reapplyModal.include_invoiced,
          },
        },
      );
      toast.success(
        `Re-applied ${data.repriced} DEFD transaction(s)`
        + (data.total_delta_cad
            ? ` · net Δ $${data.total_delta_cad.toFixed(2)} CAD`
            : ""),
      );
      setReapplyModal({
        open: false, preview: null, typed: "", busy: false,
        include_invoiced: false,
      });
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        detail
          ? `${status ? `[${status}] ` : ""}${detail}`
          : `Failed to re-apply${status ? ` (${status})` : ""}`,
      );
      setReapplyModal((s) => ({ ...s, busy: false }));
    }
  };

  return (
    <SectionPad>
      <PageHeader
        title="Margin rules"
        subtitle="Every customer × scope × product margin in one place. Download the Excel template, fill in your rules across any number of customers, upload back."
        actions={
          <>
            <button
              className="gf-btn gf-btn-outline"
              onClick={downloadTemplate}
              data-testid="margins-page-download-template"
              title="Download a fresh Excel template (with up-to-date dropdowns)"
            >
              Download template
            </button>
            <Can perm="margins.edit">
            <button
              className="gf-btn gf-btn-primary"
              onClick={() => setMarginUpload({ stage: "idle" })}
              data-testid="margins-page-upload"
              title="Upload a filled-in Excel template"
            >
              Upload Excel
            </button>
            </Can>
            <span className="border-l border-zinc-300 mx-1 h-6" aria-hidden="true" />
            <button
              className="gf-btn gf-btn-outline"
              onClick={downloadDefTemplate}
              data-testid="margins-page-download-def-template"
              title="Download a fresh DEF rates template (supports both cpl additive margin AND fixed $/L replace mode — pick per row via Margin Unit)"
            >
              DEF template
            </button>
            <Can perm="margins.edit">
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => setDefUpload({ stage: "idle" })}
              data-testid="margins-page-upload-def"
              title="Upload filled-in DEF fixed rates"
            >
              Upload DEF rates
            </button>
            <button
              className="gf-btn gf-btn-outline text-orange-700 border-orange-300 hover:bg-orange-50"
              onClick={openReapplyDefModal}
              data-testid="margins-page-reapply-def"
              title="Re-price every existing DEFD transaction whose stored margin no longer matches the current margin rules. Use this after uploading new DEF rates."
            >
              Re-apply DEF margins
            </button>
            </Can>
            <span className="border-l border-zinc-300 mx-1 h-6" aria-hidden="true" />
            <Link
              to="/margins/station-overrides"
              className="gf-btn gf-btn-outline"
              data-testid="margins-page-station-overrides"
              title="Audit every station-scope margin override against the customer's default. Catches stale overrides that shadow pricing-group uplifts."
            >
              Station overrides audit
            </Link>
            <Link
              to="/margins/petro-pass-us"
              className="gf-btn gf-btn-outline"
              data-testid="margins-page-petro-pass-us"
              title="Bulk view + edit every customer's Petro-Pass USA margin (¢/GAL). Portal visibility is toggled separately on each customer."
            >
              Petro-Pass USA USD/GAL margins
            </Link>
            <Link
              to="/margins/petro-pass-ca"
              className="gf-btn gf-btn-outline"
              data-testid="margins-page-petro-pass-ca"
              title="Manage Petro-Pass Canada margins (¢/L, CAD). Country lane is enforced end-to-end — a CA margin cannot price a US row."
            >
              Petro-Pass Canada CAD/L margins
            </Link>
          </>
        }
      />

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <input
          className="gf-input md:col-span-2"
          placeholder="Search customer, station, city, province…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="margins-search"
        />
        <select
          className="gf-input"
          value={fCustomer}
          onChange={(e) => setFCustomer(e.target.value)}
          data-testid="margins-filter-customer"
        >
          <option value="">All customers ({customers.length})</option>
          {customers.map((c) => (
            <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>
          ))}
        </select>
        <select className="gf-input" value={fNetwork} onChange={(e) => setFNetwork(e.target.value)} data-testid="margins-filter-network">
          <option value="">All networks</option>
          <option value="esso">Esso CAD/L</option>
          <option value="pipeline">Pipeline CAD/L</option>
          <option value="ta_petro">Esso USD/GAL</option>
          <option value="ANY">ANY</option>
        </select>
        <select className="gf-input" value={fScope} onChange={(e) => setFScope(e.target.value)} data-testid="margins-filter-scope">
          <option value="">All scopes</option>
          <option value="default">default</option>
          <option value="province">province</option>
          <option value="city">city</option>
          <option value="station">station</option>
        </select>
      </div>
      <div className="flex justify-between items-center mb-2 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <select className="gf-input" style={{ maxWidth: 180 }} value={fProduct} onChange={(e) => setFProduct(e.target.value)} data-testid="margins-filter-product">
            <option value="">All products</option>
            <option value="DIESEL_LS">DIESEL_LS</option>
            <option value="DSL_EFF_LS">DSL_EFF_LS</option>
            <option value="DIESEL_DYED">DIESEL_DYED</option>
            <option value="RFR">RFR</option>
            <option value="DEFD">DEFD</option>
            <option value="GAS">GAS (REG / MID / PREM)</option>
            <option value="ANY">ANY</option>
          </select>
          <div className="flex items-center gap-2">
            <span className="overline">EFFECTIVE ON</span>
            <input
              type="date"
              className="gf-input"
              style={{ maxWidth: 160 }}
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              data-testid="margins-filter-as-of-date"
              title="Show only the rule that was in effect on this date for each customer/scope/network/product/location"
            />
            {asOfDate && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-900 underline"
                onClick={() => setAsOfDate("")}
                data-testid="margins-clear-as-of-date"
                title="Clear date filter"
              >
                clear
              </button>
            )}
          </div>
        </div>
        <div className="text-xs font-mono text-zinc-500" data-testid="margins-result-count">
          {loading ? "LOADING…" : `${filtered.length} OF ${rules.length} RULES${asOfDate ? ` · effective on ${asOfDate}` : ""}`}
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table">
          <thead>
            <tr>
              <th>CUSTOMER</th>
              <th>SCOPE</th>
              <th>NET</th>
              <th>PRODUCT</th>
              <th>STATION / CITY / PROV</th>
              <th className="text-right">CPL</th>
              <th>EFFECTIVE</th>
              <th>ENDS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={9} className="text-center text-zinc-400 py-8">
                {rules.length === 0
                  ? "No margin rules yet — click 'Download template' to get started."
                  : "No rules match the current filters."}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.margin_id} data-testid={`margin-row-${r.margin_id}`}>
                <td>
                  <Link
                    to={`/customers/${r.customer_id}`}
                    className="text-zinc-900 hover:underline"
                    title="Open customer"
                  >
                    {customerNameById[r.customer_id] || r.customer_id}
                  </Link>
                </td>
                <td><Badge tone={r.scope === "station" ? "amber" : r.scope === "city" ? "blue" : r.scope === "province" ? "purple" : "zinc"}>{r.scope}</Badge></td>
                <td className="font-mono text-xs uppercase">{r.network}</td>
                <td className="font-mono text-xs">{displayProduct(r.product_canonical, r.network) || "ANY"}</td>
                <td className="text-xs">
                  {r.scope === "station"
                    ? <>{r.station_id} {r.station_name && <span className="text-zinc-500">· {r.station_name}</span>}{r.station_city && <span className="text-zinc-400"> · {r.station_city}</span>}</>
                    : r.scope === "city" ? r.city
                    : r.scope === "province" ? r.province
                    : <span className="text-zinc-400">—</span>}
                </td>
                <td className="gf-num">
                  {r.pricing_mode === "replace" ? (
                    <span title="DEF fixed rate — customer pays this $/L directly (pricing_mode=replace)">
                      <span className="font-mono">${(Number(r.margin_cpl ?? 0) / 100).toFixed(4)}</span>
                      <Badge tone="emerald" style={{ marginLeft: 6 }}>FIXED $/L</Badge>
                    </span>
                  ) : r.product_canonical === "DEFD" ? (
                    // Phase 3E-9P.2 — DEF now supports additive margin
                    // (pricing_mode=add + cpl). Badge DEF·CPL so the
                    // row is instantly distinguishable from a fixed-price
                    // DEF row above AND from a diesel cpl row below.
                    <span title="DEF additive margin — customer pays rack DEF + this cpl (pricing_mode=add)">
                      <span className="font-mono">{Number(r.margin_cpl ?? 0).toFixed(2)}</span>
                      <Badge tone="blue" style={{ marginLeft: 6 }}>DEF · CPL</Badge>
                    </span>
                  ) : (
                    Number(r.margin_cpl ?? 0).toFixed(2)
                  )}
                </td>
                <td className="font-mono text-xs">{r.effective_date}</td>
                <td className="font-mono text-xs">
                  {r.end_date
                    ? <span title="Rule expires on this date — transactions after this are not affected" className="text-rose-700">{r.end_date}</span>
                    : <span className="text-zinc-300">—</span>}
                </td>
                <td className="text-right">
                  <button
                    className="text-xs text-rose-600 hover:underline"
                    onClick={() => removeRule(r.margin_id)}
                    data-testid={`margin-row-delete-${r.margin_id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {marginUpload && (
        <MarginUploadModal
          state={marginUpload}
          setState={setMarginUpload}
          onCommitted={() => { setMarginUpload(null); load(); }}
          contextLabel="ALL CUSTOMERS"
        />
      )}
      {defUpload && (
        <MarginUploadModal
          state={defUpload}
          setState={setDefUpload}
          onCommitted={() => { setDefUpload(null); load(); }}
          contextLabel="DEF FIXED RATES"
          kind="def"
        />
      )}

      {/* Re-apply DEF margins modal (Option B — 2026-06-01).
          Shows live per-customer drift breakdown and requires the
          operator to type `REAPPLY <N> DEFD` before the destructive
          button enables. The N is recomputed from the live preview
          so race-conditions can't slip a stale count past the gate. */}
      {reapplyModal.open && reapplyModal.preview && (() => {
        const N = reapplyModal.preview.affected_count;
        const required = `REAPPLY ${N} DEFD`;
        const typedOk = reapplyModal.typed.trim() === required;
        const totalDelta = reapplyModal.preview.total_delta_cad;
        return (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !reapplyModal.busy && setReapplyModal({
              open: false, preview: null, typed: "", busy: false,
              include_invoiced: false,
            })}
            data-testid="reapply-defd-modal"
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-zinc-900">
                  Re-apply DEF margins to {N} transaction{N === 1 ? "" : "s"}
                </h2>
                <p className="text-sm text-zinc-600 mt-1">
                  These are existing DEFD transactions whose stored
                  margin doesn't match what the current margin rules
                  would compute today. The pricing engine, tax engine,
                  and report logic are untouched — only the affected
                  rows' priced fields are refreshed.
                </p>
              </div>

              <div className="mb-3 grid grid-cols-3 gap-2 text-sm">
                <div className="rounded border border-zinc-200 p-2">
                  <div className="text-xs text-zinc-500">Affected tx</div>
                  <div className="font-mono font-semibold">{N}</div>
                </div>
                <div className="rounded border border-zinc-200 p-2">
                  <div className="text-xs text-zinc-500">Total volume</div>
                  <div className="font-mono font-semibold">
                    {reapplyModal.preview.total_litres.toLocaleString()} L
                  </div>
                </div>
                <div className={`rounded border p-2 ${totalDelta > 0 ? "border-emerald-300 bg-emerald-50" : totalDelta < 0 ? "border-red-300 bg-red-50" : "border-zinc-200"}`}>
                  <div className="text-xs text-zinc-500">Net Δ revenue</div>
                  <div className="font-mono font-semibold">
                    {totalDelta >= 0 ? "+" : ""}${totalDelta.toFixed(2)} CAD
                  </div>
                </div>
              </div>

              <div className="mb-3 border rounded overflow-y-auto flex-1 min-h-32">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 sticky top-0 z-10">
                    <tr className="text-left text-zinc-600 border-b">
                      <th className="px-2 py-2">Customer</th>
                      <th className="px-2 py-2 text-right">tx</th>
                      <th className="px-2 py-2 text-right">Δ ($ CAD)</th>
                    </tr>
                  </thead>
                  <tbody data-testid="reapply-defd-customer-list">
                    {reapplyModal.preview.customers.map((c) => (
                      <tr key={c.customer_id} className="border-b last:border-0">
                        <td className="px-2 py-1.5">{c.company_name || c.customer_id}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{c.tx_count}</td>
                        <td className={`px-2 py-1.5 text-right font-mono ${c.delta_total > 0 ? "text-emerald-700" : c.delta_total < 0 ? "text-red-700" : ""}`}>
                          {c.delta_total >= 0 ? "+" : ""}${c.delta_total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mb-3 text-sm text-zinc-700">
                Type <code className="bg-zinc-100 px-1 rounded">{required}</code> to enable the button:
              </div>
              <input
                type="text"
                value={reapplyModal.typed}
                onChange={(e) => setReapplyModal((s) => ({ ...s, typed: e.target.value }))}
                disabled={reapplyModal.busy}
                className="w-full border border-zinc-300 rounded px-3 py-2 font-mono text-sm"
                placeholder={required}
                data-testid="reapply-defd-typed-input"
                autoFocus
              />

              <div className="mt-5 flex items-center justify-between gap-2">
                <div className="text-xs text-zinc-500">
                  Invoiced rows are skipped by default to avoid breaking
                  closed invoices.
                </div>
                <div className="flex gap-2">
                  <button
                    className="gf-btn gf-btn-outline"
                    onClick={() => setReapplyModal({
                      open: false, preview: null, typed: "", busy: false,
                      include_invoiced: false,
                    })}
                    disabled={reapplyModal.busy}
                    data-testid="reapply-defd-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    className={`gf-btn ${typedOk ? "gf-btn-primary bg-orange-600 hover:bg-orange-700 border-orange-700 text-white" : "gf-btn-outline opacity-50"}`}
                    onClick={executeReapplyDef}
                    disabled={!typedOk || reapplyModal.busy}
                    data-testid="reapply-defd-confirm"
                  >
                    {reapplyModal.busy ? "Re-applying…" : `Re-apply ${N} DEFD tx`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </SectionPad>
  );
}
